import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import SelfieCapture from './SelfieCapture';
import CountryCodePicker from './CountryCodePicker';
import {validateName, validateMobileNumber} from '../../utils/helpers';
import {registerUser} from '../../services/api';

const RegistrationForm = ({uuid, eventDetails}) => {
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [mobileNumber, setMobileNumber] = useState('');
    const [countryCode, setCountryCode] = useState('91'); // Default to India country code
    const [agreeTerms, setAgreeTerms] = useState(false);
    const [showCamera, setShowCamera] = useState(false);
    const [selfieImage, setSelfieImage] = useState(null);
    const [selfiePreview, setSelfiePreview] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const isFormValid = () => {
        if (!validateName(name)) {
            setError('Please enter your name (at least 2 characters).');
            return false;
        }

        if (!validateMobileNumber(mobileNumber)) {
            setError('Please enter a valid  mobile number.');
            return false;
        }

        if (!agreeTerms) {
            setError('Please agree to the terms and conditions.');
            return false;
        }

        setError('');
        return true;
    };

    const handleOpenCamera = () => {
        if (!isFormValid()) return;
        setShowCamera(true);
    };

    const handleSelfieCapture = (file, preview) => {
        setSelfieImage(file);
        setSelfiePreview(preview);
        setShowCamera(false);
    };

    const handleCloseCamera = () => {
        setShowCamera(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isFormValid()) return;

        if (!selfieImage) {
            setError('Please take a selfie to continue.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await registerUser(uuid, {
                name,
                mobileNumber,
                countryCode,
                selfieImage
            });

            if (response.result) {
                navigate(`/event/${uuid}/registration-result/success`, {
                    state: {
                        name,
                        mobileNumber,
                        countryCode,
                        securePin: response.datsecure_pin,
                        selfiePreview
                    }
                });
            } else {
                setError(response.message || 'Registration failed. Please try again.');
                setLoading(false);
            }
        } catch (err) {
            setError('Registration failed. Please try again later.');
            console.error('Registration error:', err);
            setLoading(false);
        }
    };

    return (
        <div className="registration-container">
            {showCamera ? (
                <SelfieCapture
                    onCapture={handleSelfieCapture}
                    onClose={handleCloseCamera}
                />
            ) : (
                <form onSubmit={handleSubmit} className="registration-form">
                    <div className="form-header">
                        {eventDetails?.groom_name && eventDetails?.bride_name && (
                            <h2 className="couple-names">
                                {eventDetails.groom_name} <span className="ampersand">&</span> {eventDetails.bride_name}
                            </h2>
                        )}
                        <h3 className="form-title">Get your photos from the event</h3>
                        <p className="form-subtitle">
                            Capture your selfie in our system, and we'll make sure you get every photo of yourself.
                            Our AI and Face Recognition technology make it happen!
                        </p>
                    </div>

                    {error && (
                        <div className="error-message">
                            <p>{error}</p>
                        </div>
                    )}

                    <div className="input-group">
                        <div className="input-icon">
                            <i className="fa fa-user"></i>
                        </div>
                        <input
                            type="text"
                            className="input input-with-icon"
                            placeholder="Your Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                        />
                    </div>

                    <div className="phone-input-group">
                        <CountryCodePicker
                            selectedCode={countryCode}
                            onSelect={setCountryCode}
                        />
                        <div className="input-icon">
                            <i className="fa fa-whatsapp"></i>
                        </div>
                        <input
                            type="tel"
                            className="input input-with-icon phone-input"
                            placeholder="Your WhatsApp Number"
                            value={mobileNumber}
                            onChange={(e) => setMobileNumber(e.target.value)}
                            required
                        />
                    </div>

                    <div className="checkbox-group">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={agreeTerms}
                                onChange={(e) => setAgreeTerms(e.target.checked)}
                            />
                            <span>
                                I agree to the <a href="#">Terms of Service</a>. Learn about how we use and protect your data in our <a
                                href="#">Privacy Policy</a>.
                            </span>
                        </label>
                    </div>

                    {selfiePreview ? (
                        <div className="selfie-preview">
                            <h3>Your Selfie</h3>
                            <div className="avatar">
                                <img src={selfiePreview} alt="Your selfie"/>
                            </div>
                            <div className="center">
                                <button type="button" className="button button-outlined button-icon"
                                        onClick={handleOpenCamera}>
                                    <i className="fa fa-camera"></i>
                                    Retake Selfie
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button
                            type="button"
                            className="button button-icon selfie-button"
                            onClick={handleOpenCamera}
                        >
                            <i className="fa fa-camera"></i>
                            Upload Selfie
                        </button>
                    )}

                    <button
                        type="submit"
                        className="button button-primary button-icon"
                        disabled={loading || !selfieImage}
                    >
                        {loading ? (
                            <>
                                <i className="fa fa-spinner fa-spin"></i>
                                Processing...
                            </>
                        ) : (
                            <>
                                <i className="fa fa-check"></i>
                                Register
                            </>
                        )}
                    </button>

                    <div className="info-text">
                        <small>
                            By clicking register, you give us permission to use AI facial recognition to securely
                            deliver event photos directly to you.
                        </small>
                    </div>
                </form>
            )}
        </div>
    );
};

export default RegistrationForm;